import p2 from 'p2';
import { Container, Sprite, Texture } from 'pixi.js';

import { Shared } from '../../App';
import { Settings } from '../../AppConfig';
import { DisplayUtils } from '../../utils/DisplayUtils';
import { Random } from '../../utils/Random';
import { BlinkComponent } from '../components/BlinkComponent';
import { SpineComponent } from '../components/SpineComponent';
import { GHOST_COLOR, GHOST_COLOR_MIX } from '../Game';
import { PhysicsCollisionGroup } from '../physics';
import { PhysicsComponent } from '../physics/PhysicsComponent';
import { PhysicsShapeData, PhysicsUtils } from '../physics/PhysicsUtils';
import { ReplayBodyComponent } from '../replay';
import { BaseEntity } from './BaseEntity';

export interface CharacterData
{
    id: string,
    available?: boolean,
    iconId: string,
    type: string,
    mass: number,
    seatX: number,
    seatY: number,
    sprites?: {id: string, mode: string, tint?: string}[],
    shapes: PhysicsShapeData[],
    ghost?: boolean,
    spine: string,
    spineSkinName?: string,
    spineScale: number
    spineX: number,
    spineY: number,
    spineStaticCrash?: boolean
    unlocked?: boolean;
    name: string,
    logoAsset: string,
    brand: string,
    new?: boolean
    coinPrice: number,
    diamondPrice: number,
}

export class CharacterEntity extends BaseEntity<CharacterData>
{
    public physics: PhysicsComponent;
    public replay: ReplayBodyComponent;
    public blink: BlinkComponent;
    public spine: SpineComponent;
    public ejected = false;
    private seatPos?: Sprite;

    constructor()
    {
        super();
        this.physics = this.addComponent(PhysicsComponent);
        this.replay = this.addComponent(ReplayBodyComponent);
        this.blink = this.addComponent(BlinkComponent);
        this.spine = this.addComponent(SpineComponent);
    }

    public build(data: CharacterData, mode = 'car'): void
    {
        this.data = data;
        this.spine.clear();
        this.view.removeChildren();

        if (!data.sprites)
        {
            this.spine.setup(`spine-${data.spine}`, {
                x: data.spineX,
                y: data.spineY,
                scale: data.spineScale,
                skin: data.spineSkinName,
                bikeMode: mode === 'bike',
            });
        }
        else
        {
            CharacterEntity.mountSprite(this.view, data, mode);
        }

        if (this.physics.mainBody) return;
        this.physics.clearParts();

        const body = PhysicsUtils.createBody({
            mass: data.ghost ? 0 : 0.1,
        }, ...data.shapes);

        const material = data.ghost ? undefined : Shared.game.physics.contact.getMaterial({
            name: `character`,
            friction: 30,
        });

        PhysicsUtils.setupShapes(body.shapes, {
            group: data.ghost ? 0 : PhysicsCollisionGroup.VEHICLE,
            mask: data.ghost ? 0 : PhysicsCollisionGroup.LEVEL,
            material: data.ghost ? undefined : material,
            sensor: data.ghost,
        });

        // body.collisionResponse = false;
        this.physics.addParts(body);
        this.ejected = false;
        this.replay.suspendPlayback = false;
        this.z = 0;
        this.view.scale.set(1);
        this.view.renderable = true;

        if (Shared.game.debug)
        {
            if (!this.seatPos) this.seatPos = new Sprite(Texture.WHITE);
            this.seatPos.anchor.set(0.5);
            this.seatPos.scale.set(0.5);
            this.seatPos.tint = 0xFF0000;
            this.seatPos.alpha = 0.9;
            this.seatPos.x = data.seatX;
            this.seatPos.y = data.seatY;
            this.view.addChild(this.seatPos);
        }
    }

    public updateColor(): void
    {
        if (!this.spine.spine) return;

        this.spine.spine.tint = DisplayUtils.mixColors(0xFFFFFF, GHOST_COLOR, this.data?.ghost ? GHOST_COLOR_MIX : 0);
    }

    protected onBeginContact(other: p2.Body): void
    {
        if (!this.game || Settings.god) return;

        if (!this.ejected && (other.harmPlayer || other.physicsComponent?.harmPlayer))
        {
            this.game.vehicle.trick.trickReset();
            this.game.vehicle.motor.brake(1);

            const groundCollision = other.physicsComponent?.ground;
            
            if (groundCollision && !Settings.groundCrash && !this.game.vehicle.finished)
            {
                // Ignore this collision if Settings.groundCrash is false and vehicle has not finished the race
                // We still wants the vehicle crash on ground after race is finished anyway
                return;
            }

            this.game.vehicle.crash();
        }
    }

    protected onEndContact(other: p2.Body): void
    {
        if (!this.game || Settings.god) return;

        if (other.physicsComponent?.ground)
        {
            this.game.vehicle.trick.trickReset();
            this.game.vehicle.motor.brake(1);
        }
    }

    public static mountSprite(container: Container, data: CharacterData, mode = 'car', anim = 'Celebrate'): Container
    {
        if (!data.sprites)
        {
            const spine = SpineComponent.getSpineSprite(`spine-${data.spine}`, {
                x: data.spineX,
                y: data.spineY,
                scale: data.spineScale,
                skin: data.spineSkinName,
                bikeMode: mode === 'bike',
                anim,
            });

            container.addChild(spine);
        }
        else
        {
            for (const spriteData of data.sprites)
            {
                if (spriteData.mode !== mode) continue;

                const sprite = Sprite.from(Shared.assets.getTexture(spriteData.id));

                sprite.anchor.set(0.5);
                sprite.x = data.spineX;
                sprite.y = data.spineY;
                sprite.tint = 0xFFFFFF;
                container.addChild(sprite);
            }
        }

        return container;
    }

    public async eject(): Promise<void>
    {
        if (this.ejected) return;
        this.ejected = true;
        this.replay.suspendPlayback = true;
        this.spine.playFall();

        if (!this.game) return;

        this.physics.clearForces();
        setTimeout(() => this.physics.clearForces(), 10);
        const x = this.physics.x + Random.range(-500, 500);
        const y1 = this.physics.y - Random.range(300, 350);
        const y2 = this.physics.y + 800;
        const rotation = Random.pick(-10, -5, 5, 10);
        const scale = 2;
        const duration = 1.5;

        this.physics.mainBody.gravityScale = 0;
        this.z = 0.1;

        this.game.tween.to(this.physics, { x, duration, ease: 'linear' });
        this.game.tween.to(this.view.scale, { x: scale, y: scale, duration, ease: 'linear' });
        this.game.tween.to(this.view, { rotation, duration, ease: 'linear' });
        this.game.tween.to(this.view, { alpha: 0, duration: 0.2, ease: 'linear', delay: duration - 0.2 });
        await this.game.tween.to(this.physics, { y: y1, duration: duration * 0.4, ease: 'sine.out' });
        await this.game.tween.to(this.physics, { y: y2, duration: duration * 0.6, ease: 'sine.in' });
        this.view.renderable = false;
    }

    public recover(): void
    {
        if (!this.game) return;
        this.ejected = false;
        this.game.tween.kill(this.physics);
        this.game.tween.kill(this.view.scale);
        this.game.tween.kill(this.view);
        this.physics.mainBody.gravityScale = 1;
        this.replay.suspendPlayback = false;
        this.view.scale.set(1);
        this.view.alpha = 1;
        this.spine.playIdle();
        this.view.renderable = true;
        this.game.race.updateVehiclesOrder();
    }
    
    public cleanup(): void
    {
        this.spine.cleanup();
    }
}
